/**
 * Hard-coded allowlist of tenants permitted to use the offline-checks
 * feature.
 *
 * The feature ships locked to Bakker Barendrecht — even if another tenant's
 * `root_config.enable_offline_checks` ends up `true` (manual DB edit,
 * accidental admin toggle), the runtime gate keeps the feature off and the
 * admin GUI hides the toggles. To roll the feature out to a new tenant, add
 * its UUID here; do **not** remove the gate.
 *
 * The playwright-test tenant is included so the e2e suite (which runs
 * against its own seeded tenant, not Bakker) keeps working. The UUID is the
 * same `TEST_TENANT_ID` used by `backend/tools/seed_playwright_tenant.py`
 * and `e2e/fixtures/db.ts`. It is intentionally compiled into every build
 * (including production) — a real customer tenant cannot collide with this
 * v4-shaped UUID by accident, and there is no harm in carrying it.
 *
 * Single source of truth: both the runtime gate (`isOfflineChecksEnabled`
 * in `./db`) and the admin GUI (Root/OverviewTab/CheckLocation config
 * forms) read from here.
 */

import { tenantIds } from "../actions/Tenants/config/constants";

/** Mirrors `TEST_TENANT_ID` in `backend/tools/seed_playwright_tenant.py`. */
const PLAYWRIGHT_TEST_TENANT_ID = "e2e7e571-aaaa-4aaa-aaaa-aaaaaaaaaaaa";

export const OFFLINE_CHECKS_ALLOWED_TENANT_IDS: ReadonlySet<string> = new Set([
    tenantIds.Bakker, // Bakker Barendrecht aug 2022 — production rollout target
    tenantIds.BakkerSAP, // Bakker SAP / LW100 — production rollout target
    PLAYWRIGHT_TEST_TENANT_ID, // playwright-test — e2e suite seed tenant
]);

/**
 * True when the given tenant_id is on the offline-checks allowlist.
 * Tolerant of unknown input shape (the `config` object the gate is called
 * with is loosely typed); only a non-empty string in the allowlist passes.
 */
export function isOfflineChecksAllowedForTenant(tenant_id: unknown): boolean {
    return typeof tenant_id === "string"
        && OFFLINE_CHECKS_ALLOWED_TENANT_IDS.has(tenant_id);
}
